import Autoplay from 'embla-carousel-autoplay'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Banner } from './banner/Banner'
import styles from './BannersCarousel.module.scss'
import { ControlIcon } from './controlIcon/ControlIcon'
import { Embla, EmblaCarousel } from '@percent/lemonade'
import type { MarketplaceBannerCarouselItem } from '@percent/cause-dashboard/storyblok/product-offers/banners/mapStoryblokBannerSlideToBannerProps'
import { useCausesPortalAnalytics } from '@percent/cause-dashboard/common/hooks/useCausesDashboardAnalytics/useCausesDashboardAnalytics'
import { GFM_INTEREST_EVENTS } from '@percent/cause-dashboard/common/components/gfmInterest/gfmInterestEvents'

const AUTOPLAY_DELAY = 8000
const TRANSITION_SPEED = 10

export type BannersCarouselProps = {
  slides: MarketplaceBannerCarouselItem[]
}

export function BannersCarousel({ slides }: Readonly<BannersCarouselProps>) {
  const items = useMemo(
    () =>
      slides.map(({ id, controls, ...banner }) => ({
        id,
        content: <Banner key={id} {...banner} />
      })),
    [slides]
  )

  const [emblaApi, setEmblaApi] = useState<Embla | null>(null)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [controlsVariant, setControlsVariant] = useState(slides[0]?.controls ?? 'dark')

  const autoplay = useRef(Autoplay({ delay: AUTOPLAY_DELAY }))

  const { track } = useCausesPortalAnalytics()
  const hasTrackedGfmView = useRef(false)
  const firstSlideVariant = slides[0]?.experimentVariant

  useEffect(() => {
    if (!firstSlideVariant || hasTrackedGfmView.current) {
      return
    }
    hasTrackedGfmView.current = true
    track(GFM_INTEREST_EVENTS.shown, { variant: firstSlideVariant, element: 'carousel' })
  }, [firstSlideVariant, track])

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on('select', _ => {
        setCurrentSlideIndex(emblaApi.selectedScrollSnap())
      })
    }
  }, [emblaApi])

  useEffect(() => {
    autoplay.current.reset()
    const current = slides[currentSlideIndex]
    setControlsVariant(current?.controls ?? 'dark')
  }, [currentSlideIndex, slides])

  if (slides.length === 0) {
    return null
  }

  return (
    <div className={styles.carouselContainer} data-testid="banners-carousel">
      <EmblaCarousel
        items={items}
        plugins={[autoplay.current]}
        speed={TRANSITION_SPEED}
        height="100%"
        draggable={false}
        previousControlIcon={<ControlIcon direction="previous" variant={controlsVariant} />}
        nextControlIcon={<ControlIcon direction="next" variant={controlsVariant} />}
        className={styles.carousel}
        classNames={{
          controls: styles.controls,
          control: styles.control
        }}
        getEmblaApi={setEmblaApi}
      />
    </div>
  )
}
